"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const TRANSITIONS = [
  { from: "Draft", to: "Sent, Cancelled" },
  { from: "Sent", to: "Accepted, Rejected, Draft, Cancelled" },
  { from: "Accepted", to: "Completed, Sent, Draft, Cancelled" },
  { from: "Rejected", to: "Draft, Cancelled" },
  { from: "Expired", to: "Cancelled" },
  { from: "Completed", to: "Cancelled" },
  { from: "Cancelled", to: "Draft, Sent" },
];

const RULES = [
  {
    topic: "No ledger posting",
    rule: "A quote never posts to the general ledger. Only the Sales Order or Invoice created from it does",
  },
  {
    topic: "Subscription gate",
    rule: "Only creating a quote is limited by your plan. Editing, sending, changing status, converting, duplicating, and deleting are not",
  },
  {
    topic: "A primary bank account is required to create a quote",
    rule: "If your company hasn't set up a primary bank account yet, you can't create a quote at all — mark one under Banking → Banks with Make this primary. The bank details print on the PDF",
  },
  {
    topic: "Quote number",
    rule: "Auto-generated with a QUO prefix unless you turn that off under Quote Configuration on the form. That same drawer also stores Terms & Conditions, which pre-fill every new quote. Locked after the quote is saved",
  },
  {
    topic: "Division, Branch, Warehouse, Customer",
    rule: "All four are required to create. After save they are locked — edit a Draft to change items and amounts, not the customer or location",
  },
  {
    topic: "Currency",
    rule: "Taken from the customer at creation. An exchange rate field appears when that currency differs from the company base; you can set the rate then. The customer itself can't be changed later, so the currency stays put",
  },
  {
    topic: "Expiry Date",
    rule: "Optional, but if set it must be after the Quote Date. Nothing auto-moves the quote to Expired when that date passes — Expired is not on the Mark As menu",
  },
  {
    topic: "Stock check",
    rule: "If Inventory Config has stock check on for quotes, save is blocked when the warehouse doesn't have enough. The quote does not reserve or deduct stock",
  },
  {
    topic: "Editing skips the overdue-invoice check",
    rule: "That check only runs when you first create a quote. Editing a Draft re-checks the shipping address, the credit limit, and stock, but not overdue invoices",
  },
  {
    topic: "A quote converts only once",
    rule: "Saving a Sales Order or Invoice from the quote moves it to Completed. Completed can't be converted again. One quote becomes exactly one order or one invoice, never both",
  },
  {
    topic: "Credit limit and overdue restrictions aren't quote-specific",
    rule: "Settings → Credit limits holds the company defaults. Restriction Document is the amount check (Quote, Sales Order, Delivery Challan, Invoice). Restriction Payment Terms Document is the overdue check — if Quote is listed, you cannot create a quote while that customer has an Overdue invoice. A customer can override those defaults on their own Branches tab",
  },
  {
    topic: "Cancelling vs deleting",
    rule: "Cancel is blocked while a non-cancelled Sales Order or Invoice exists from the quote. Delete is stricter — blocked if any Sales Order or Invoice exists from it, including cancelled ones",
  },
  {
    topic: "Attachments",
    rule: "Up to 3 files, 5 MB each. Editing a Draft adds new files to the existing ones; nothing is replaced unless you remove it",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. Creating a quote" },
      { heading: "2. Viewing, searching, and exporting" },
      { heading: "3. Editing a quote" },
      { heading: "4. Sending, printing, and downloading" },
      { heading: "5. Changing a quote's status" },
      { heading: "6. Converting to a Sales Order or Invoice" },
      { heading: "7. Duplicating a quote" },
      { heading: "8. Deleting a quote" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

export default function QuotesPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Quotes
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone sending a price offer to a customer before there&apos;s
            an order
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            A <B>Quote</B> is an offer — items, quantities, prices, tax, and
            terms — that you send a customer before anything is confirmed. The
            customer can <B>accept</B> it, <B>reject</B> it, or you can{" "}
            <B>cancel</B> it. Once accepted, convert it to a{" "}
            <B>Sales Order</B> or an <B>Invoice</B> from the quote itself,
            carrying the details forward instead of re-entering them.
          </p>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Creating a quote
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Pick <B>Division</B>, then <B>Branch</B>, then <B>Warehouse</B> —
            the rest of the form stays locked until those three are set. A
            scratch quote can pre-fill those three from{" "}
            <B>Settings → Organization defaults</B>. A few checks run
            automatically the moment you save.
          </p>
          <Definitions
            items={[
              {
                term: "Customer and Shipping Address",
                desc: (
                  <>
                    The address has to be <B>one of that customer&apos;s own
                    addresses</B>. You can&apos;t pick an address that belongs
                    to someone else.
                  </>
                ),
              },
              {
                term: "Quote Number",
                desc: (
                  <>
                    Auto-generated unless you turn that off under{" "}
                    <B>Quote Configuration</B> on the form (prefix and next
                    number). That same drawer stores <B>Terms & Conditions</B>,
                    which pre-fill every new quote. Locked after save. This is
                    not <B>Settings → Quotation config</B>, which is the
                    reusable list of rejection reasons.
                  </>
                ),
              },
              {
                term: "Price List",
                desc: (
                  <>
                    Optional. When you pick one, item rates come from that list
                    instead of the item&apos;s default selling price.
                  </>
                ),
              },
              {
                term: "Currency and exchange rate",
                desc: (
                  <>
                    Currency comes from the customer. If it differs from the
                    company base (set under <B>Settings → Currencies</B>), an{" "}
                    <B>Exchange Rate</B> field appears and you set it on this
                    quote.
                  </>
                ),
              },
              {
                term: "Items, discounts, charges, and tax",
                desc: (
                  <>
                    Line items with quantity and rate. Document discount follows{" "}
                    <B>Settings → Discounts</B> — Enable Discount, then Discount
                    Applies On <B>Item Level</B> or <B>Transaction Level</B>.
                    Extra lines such as freight come from{" "}
                    <B>Settings → Additional charges</B> assigned to Quotation.
                    GST (CGST / SGST / IGST) is calculated from customer state
                    vs company state. Round-off is available on the totals.
                  </>
                ),
              },
              {
                term: "Credit limit",
                desc: (
                  <>
                    If the customer has a credit limit that covers quotes
                    (Restriction Document includes Quote), the total can&apos;t
                    push them over it. Defaults live under{" "}
                    <B>Settings → Credit limits</B> until the customer overrides
                    them. You&apos;ll be told the <B>limit</B>,{" "}
                    <B>current usage</B>, and <B>how much room is left</B> if it
                    would.
                  </>
                ),
              },
              {
                term: "Overdue invoices",
                desc: (
                  <>
                    If <B>Restriction Payment Terms Document</B> includes Quote
                    (on the customer, or the company default under{" "}
                    <B>Settings → Credit limits</B>), you can&apos;t create a
                    quote while that customer has an <B>Overdue</B> invoice.
                    Pay it, or remove Quote from that list.
                  </>
                ),
              },
              {
                term: "Stock check",
                desc: (
                  <>
                    If Inventory Config has this on for quotes, save is blocked
                    when the warehouse doesn&apos;t have enough. Stock is{" "}
                    <B>not reserved</B> — a quote is still only an offer.
                  </>
                ),
              },
              {
                term: "Save vs Save & Sent",
                desc: (
                  <>
                    <B>Save</B> creates a Draft. <B>Save & Sent</B> is only on
                    create — saves and marks it <B>Sent</B> in one step.
                  </>
                ),
              },
            ]}
          />
          <Callout type="warning">
            You can&apos;t create a quote at all until your company has a{" "}
            <B>primary bank account</B> set up. Mark one under Banking → Banks
            with <B>Make this primary</B>. Those bank details print on the quote
            PDF, even if this particular quote never becomes an invoice.
          </Callout>
          <Callout type="note">
            A quote does <B>not</B> post to the ledger. Nothing hits Accounts
            Receivable or Sales until you convert it and save the Sales Order
            or Invoice.
          </Callout>
          <Screenshot caption="Screenshot: the Add Quote form, showing Division, Branch, Warehouse, Customer, and the item list." />

          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Viewing, searching, and exporting
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            The list and the detail page cover different needs.
          </p>
          <Definitions
            items={[
              {
                term: "Finding a quote",
                desc: (
                  <>
                    Status tabs across the top, plus filters for{" "}
                    <B>date range</B>, <B>status</B>, <B>customer</B>,{" "}
                    <B>added by</B>, <B>division</B>, and <B>branch</B>. Search
                    by quote number, subject, reference, or customer name. If
                    you also use ABSS CRM, a <B>Created From</B> filter
                    separates Books quotes from CRM ones.
                  </>
                ),
              },
              {
                term: "Export",
                desc: (
                  <>
                    Download the current list (respecting filters) as a
                    spreadsheet from More Actions.
                  </>
                ),
              },
              {
                term: "Opening one",
                desc: (
                  <>
                    <B>Basic Info</B> shows items, customer, shipping address,
                    and the company bank details as they print.{" "}
                    <B>Document Flow</B> shows what this quote became — Sales
                    Order, Delivery Challan, Invoice, Payment Received.{" "}
                    <B>History</B> is a running log, and you can add comments
                    there if you have edit permission.
                  </>
                ),
              },
              {
                term: "Converted To",
                desc: (
                  <>
                    If the quote became a Sales Order or Invoice, that
                    document&apos;s number, date, total, and status are shown
                    on the quote.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: a quote's detail page, showing Basic Info, Document Flow, and Convert To Sales Order / Invoice." />

          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. Editing a quote
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Only a <B>Draft</B> quote can be edited. Once it&apos;s moved past
            Draft, it&apos;s locked.
          </p>
          <Definitions
            items={[
              {
                term: "What stays locked",
                desc: (
                  <>
                    <B>Division</B>, <B>Branch</B>, <B>Warehouse</B>,{" "}
                    <B>Customer</B>, and <B>Quote Number</B> cannot be changed
                    on edit.
                  </>
                ),
              },
              {
                term: "What gets re-checked",
                desc: (
                  <>
                    The shipping address still has to belong to the customer,
                    the <B>credit limit</B> still can&apos;t be exceeded, and{" "}
                    <B>stock</B> is re-checked if that setting is on.
                  </>
                ),
              },
              {
                term: "Attachments",
                desc: (
                  <>
                    New files join the existing ones. Nothing gets replaced
                    unless you remove it yourself.
                  </>
                ),
              },
            ]}
          />
          <Callout type="note">
            The overdue-invoice check only runs when a quote is first created,
            not when you edit it afterward.
          </Callout>
          <Screenshot caption="Screenshot: editing a Draft quote, with the item list and totals open for changes." />

          <h3
            id="section-0-sub-3"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            4. Sending, printing, and downloading
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            &quot;Sending&quot; here means marking the quote as <B>Sent</B>. It
            does not email the customer.
          </p>
          <Definitions
            items={[
              {
                term: "Mark as Sent",
                desc: (
                  <>
                    Status changes to <B>Sent</B>, and who did it and when is
                    added to History. Same result as <B>Save & Sent</B> on
                    create.
                  </>
                ),
              },
              {
                term: "Getting it to the customer",
                desc: (
                  <>
                    Use <B>Download</B> (PDF) or <B>Print</B> on the detail
                    page. Those are how the offer actually leaves the app.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Download and Print on the quote detail header, next to View Customer." />

          <h3
            id="section-0-sub-4"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            5. Changing a quote&apos;s status
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            <B>Mark Quotation As</B> only offers the moves below. A couple of
            backend-allowed moves are not on this menu.
          </p>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto mb-4">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    From
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Mark As offers
                  </th>
                </tr>
              </thead>
              <tbody>
                {TRANSITIONS.map((t, i) => (
                  <tr
                    key={t.from}
                    className={
                      i < TRANSITIONS.length - 1
                        ? "border-b border-[#F5F5F5]"
                        : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[140px]">
                      {t.from}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {t.to}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Callout type="note">
            <B>Expired</B> is a real status, and an Expiry Date can be stored
            on the quote, but the Mark As menu never offers Expired, and
            nothing auto-expires the quote when that date passes. A{" "}
            <B>Cancelled</B> quote isn&apos;t a dead end — it can be brought
            back to <B>Draft</B> or <B>Sent</B>.
          </Callout>
          <Definitions
            items={[
              {
                term: "Rejecting with a reason",
                desc: (
                  <>
                    You pick or type a reason. New reasons are saved to{" "}
                    <B>Quotation Config</B> so they show up next time.
                  </>
                ),
              },
              {
                term: "Completed without converting",
                desc: (
                  <>
                    From Accepted you can mark Completed yourself. Converting
                    to a Sales Order or Invoice also moves it to Completed —
                    but only after that document is actually saved.
                  </>
                ),
              },
              {
                term: "Cancelling",
                desc: (
                  <>
                    Blocked while a <B>non-cancelled</B> Sales Order or Invoice
                    exists from this quote. You&apos;ll see which one.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Mark Quotation As on a Sent quote, showing Accepted, Rejected, Draft, and Cancelled." />

          <h3
            id="section-0-sub-5"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            6. Converting to a Sales Order or Invoice
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Only an <B>Accepted</B> quote can be converted. The buttons sit on
            the quote itself.
          </p>
          <Definitions
            items={[
              {
                term: "From the quote",
                desc: (
                  <>
                    <B>Convert To Sales Order</B> or{" "}
                    <B>Convert To Invoice</B> opens the new document with
                    customer, items, amounts, charges, and notes already
                    filled. The quote stays Accepted until you save that
                    document.
                  </>
                ),
              },
              {
                term: "One conversion, ever",
                desc: (
                  <>
                    Saving the Sales Order or Invoice moves the quote to{" "}
                    <B>Completed</B>. A Completed quote can&apos;t be converted
                    again — one quote becomes one order or one invoice, never
                    both.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Convert To Sales Order and Convert To Invoice on an Accepted quote." />

          <h3
            id="section-0-sub-6"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            7. Duplicating a quote
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            From the list or the detail page, <B>Duplicate Document</B> opens
            Add Quote with the same customer, items, and amounts.
          </p>
          <Definitions
            items={[
              {
                term: "What is left off",
                desc: (
                  <>
                    A new quote number, today&apos;s dates, Draft status, and
                    no attachments. You save it as a fresh quote.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Duplicate Document on the quote list row actions." />

          <h3
            id="section-0-sub-7"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            8. Deleting a quote
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Delete is stricter than cancel.
          </p>
          <Definitions
            items={[
              {
                term: "Blocked when",
                desc: (
                  <>
                    Any <B>Sales Order</B> or <B>Invoice</B> exists from this
                    quote — including cancelled ones. You&apos;ll be shown
                    which document is holding it.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: a delete attempt blocked because a Sales Order was already created from the quote." />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr
                    key={r.topic}
                    className={
                      i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[16px] geist-regular text-[#374151]">
                      {r.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/sales/sales-orders"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Sales Orders <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              One of the two documents an Accepted quote can become
            </p>
          </Link>
          <Link
            href="/documentation/books/sales/invoices"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Invoices <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              The other document an Accepted quote can become
            </p>
          </Link>
          <Link
            href="/documentation/books/settings/quotation-config"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Quotation Config <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              The reusable list of reasons when a quote is rejected
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
